/**
 * Color-by-Attribute model for the comparison results page (mock v16).
 *
 * A color rule paints properties (map wells + property tables) by the values of
 * one attribute. Rules are ordered; a property that matches several rules takes
 * the color of the rule listed first. The seeded default is the Origin vs Target
 * rule, matching the map's membership colors.
 */
import {
  formatCategory,
  formatStatus,
  sideMissing,
} from './property-attributes';
import { MATCH_COLORS } from './map-config';
import type { ComparisonResult, MatchStatus } from './types';

/** The attributes a color rule can key on, in the modal's list order. */
export type ColorByAttrKey =
  'originVsTarget' | 'resCategory' | 'status' | 'volumeSource' | 'assignedUnit';

export interface ColorByAttrDef {
  key: ColorByAttrKey;
  label: string;
}

export const COLOR_BY_ATTRIBUTES: ColorByAttrDef[] = [
  { key: 'originVsTarget', label: 'Origin vs Target' },
  { key: 'resCategory', label: 'Reserve Category' },
  { key: 'status', label: 'Status' },
  { key: 'volumeSource', label: 'Volume Source' },
  { key: 'assignedUnit', label: 'Assigned Unit' },
];

/** One selectable value of an attribute, with its assigned swatch color. */
export interface ColorRuleValue {
  value: string;
  label: string;
  color: string;
}

/** A single color rule shown in the sidebar and applied to the map/tables. */
export interface ColorRule {
  id: string;
  attribute: ColorByAttrKey;
  label: string;
  values: ColorRuleValue[];
  applyToMap: boolean;
  applyToTables: boolean;
  enabled: boolean;
}

// Qualitative palette for non-membership attributes. The first three match the
// Origin vs Target legend so a color-by feels consistent with the map.
export const COLOR_PALETTE = [
  '#0f62fe',
  '#8a3800',
  '#198038',
  '#a56eff',
  '#ee538b',
  '#009d9a',
  '#b28600',
  '#fa4d56',
  '#6929c4',
  '#1192e8',
  '#005d5d',
  '#9f1853',
];

const ORIGIN_TARGET_VALUES: { value: MatchStatus; label: string }[] = [
  { value: 'matched', label: 'In Both' },
  { value: 'unmatched_a', label: 'Only in Origin' },
  { value: 'unmatched_b', label: 'Only in Target' },
];

const ORIGIN_TARGET_LABEL: Record<string, string> = {
  matched: 'In Both',
  unmatched_a: 'Only in Origin',
  unmatched_b: 'Only in Target',
};

// The Origin/Target sides that carry data for a result (a missing side belongs
// to the "Only in …" case and contributes no attribute values).
const presentSides = (r: ComparisonResult): Array<'a' | 'b'> => {
  const sides: Array<'a' | 'b'> = [];
  if (!sideMissing(r.matchStatus, 'a')) sides.push('a');
  if (!sideMissing(r.matchStatus, 'b')) sides.push('b');
  return sides;
};

const dedupe = (values: string[]): string[] => Array.from(new Set(values));

const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

interface Accessor {
  /** The canonical attribute values a result carries (across present sides). */
  values: (r: ComparisonResult) => string[];
  /** Display label for a canonical value. */
  label: (value: string) => string;
  /** Fixed color for a value (Origin vs Target membership), else palette-assigned. */
  colorFor?: (value: string) => string | undefined;
  /** Canonical value order for the value list (else alphabetical by label). */
  fixedOrder?: string[];
}

const ACCESSORS: Record<ColorByAttrKey, Accessor> = {
  originVsTarget: {
    values: (r) => [r.matchStatus],
    label: (v) => ORIGIN_TARGET_LABEL[v] ?? v,
    colorFor: (v) => MATCH_COLORS[v as MatchStatus],
    fixedOrder: ['matched', 'unmatched_a', 'unmatched_b'],
  },
  resCategory: {
    values: (r) =>
      dedupe(
        presentSides(r).map((s) => {
          const raw = r.attributes.resCategory[s];
          return hasText(raw) ? raw : 'undefined';
        })
      ),
    label: (v) => formatCategory(v),
  },
  status: {
    values: (r) =>
      dedupe(
        presentSides(r)
          .map((s) => r.attributes.status[s])
          .filter(hasText)
      ),
    label: (v) => formatStatus(v),
  },
  volumeSource: {
    values: (r) =>
      dedupe(
        presentSides(r)
          .map((s) => r.attributes.volumeSource[s])
          .filter(hasText)
      ),
    label: (v) => v,
  },
  assignedUnit: {
    values: (r) =>
      dedupe(
        presentSides(r).map((s) => {
          const raw = r.attributes.assignedUnit[s];
          return hasText(raw) ? raw : 'Unassigned';
        })
      ),
    label: (v) => v,
  },
};

// The canonical values a result carries for an attribute.
export const attrValues = (
  result: ComparisonResult,
  attr: ColorByAttrKey
): string[] => ACCESSORS[attr].values(result);

// Display label for a canonical attribute value.
export const attrValueLabel = (attr: ColorByAttrKey, value: string): string =>
  ACCESSORS[attr].label(value);

export interface AttrValueOption {
  value: string;
  label: string;
}

/**
 * The distinct values an attribute takes across the comparison, in list order
 * (membership order for Origin vs Target, else alphabetical by label).
 * @param {ComparisonResult[]} results - The comparison results
 * @param {ColorByAttrKey} attr - The attribute to enumerate
 * @return {AttrValueOption[]} The selectable value options
 */
export const buildAttrValueOptions = (
  results: ComparisonResult[],
  attr: ColorByAttrKey
): AttrValueOption[] => {
  const acc = ACCESSORS[attr];
  const set = new Set<string>();
  results.forEach((r) => acc.values(r).forEach((v) => set.add(v)));
  let values = Array.from(set);
  if (acc.fixedOrder) {
    values = acc.fixedOrder.filter((v) => set.has(v));
  } else {
    values.sort((a, b) => acc.label(a).localeCompare(acc.label(b)));
  }
  return values.map((value) => ({ value, label: acc.label(value) }));
};

/**
 * How many properties carry a given attribute value (either side).
 * @param {ComparisonResult[]} results - The comparison results
 * @param {ColorByAttrKey} attr - The attribute
 * @param {string} value - The canonical value
 * @return {number} The count of matching properties
 */
export const countAttrValue = (
  results: ComparisonResult[],
  attr: ColorByAttrKey,
  value: string
): number =>
  results.reduce(
    (n, r) => (ACCESSORS[attr].values(r).includes(value) ? n + 1 : n),
    0
  );

/**
 * The swatch color for a value: fixed for Origin vs Target membership, else the
 * palette entry at the value's selection index.
 * @param {ColorByAttrKey} attr - The attribute
 * @param {string} value - The canonical value
 * @param {number} index - The value's index in the rule
 * @return {string} A hex color
 */
export const colorForValue = (
  attr: ColorByAttrKey,
  value: string,
  index: number
): string =>
  ACCESSORS[attr].colorFor?.(value) ??
  COLOR_PALETTE[index % COLOR_PALETTE.length];

/**
 * The color a property should take from an ordered list of rules: the first
 * enabled rule whose selected values include one of the property's values wins.
 * @param {ComparisonResult} result - The property
 * @param {ColorRule[]} rules - The rules to consider (already surface-filtered)
 * @return {string | null} The resolved color, or null if no rule matches
 */
export const resolveRuleColor = (
  result: ComparisonResult,
  rules: ColorRule[]
): string | null => {
  for (const rule of rules) {
    if (!rule.enabled) continue;
    const values = ACCESSORS[rule.attribute].values(result);
    const match = rule.values.find((rv) => values.includes(rv.value));
    if (match) return match.color;
  }
  return null;
};

let idCounter = 0;
export const makeColorRuleId = (): string => {
  idCounter += 1;
  return `color-rule-${idCounter}`;
};

const labelForAttr = (attr: ColorByAttrKey): string =>
  COLOR_BY_ATTRIBUTES.find((a) => a.key === attr)?.label ?? attr;

/**
 * Builds a color rule from an attribute and its chosen values.
 * @param {object} spec - The rule fields
 * @return {ColorRule} The assembled rule
 */
export const buildColorRule = (spec: {
  attribute: ColorByAttrKey;
  values: ColorRuleValue[];
  applyToMap: boolean;
  applyToTables: boolean;
  id?: string;
  enabled?: boolean;
}): ColorRule => ({
  id: spec.id ?? makeColorRuleId(),
  attribute: spec.attribute,
  label: labelForAttr(spec.attribute),
  values: spec.values,
  applyToMap: spec.applyToMap,
  applyToTables: spec.applyToTables,
  enabled: spec.enabled ?? true,
});

// The seeded default rule: Origin vs Target, matching the map legend.
export const defaultOriginTargetRule = (): ColorRule =>
  buildColorRule({
    id: 'color-rule-origin-target',
    attribute: 'originVsTarget',
    values: ORIGIN_TARGET_VALUES.map((v) => ({
      value: v.value,
      label: v.label,
      color: MATCH_COLORS[v.value],
    })),
    applyToMap: true,
    applyToTables: true,
  });
